import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    FileText, Calendar as CalendarIcon, Table as TableIcon, Sparkles, Loader2,
    Check, X as XIcon, ChevronLeft, ChevronRight, ClipboardList, Download, UserCheck, Plus,
    Trash2, Undo2, AlertCircle, ShieldCheck, ShieldAlert, Eye, Send, Gavel,
} from 'lucide-react';

const ACCENT = '#7C6BEF';

const STATUS_META = {
    pending: { label: 'Pendiente', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
    assigned: { label: 'Asignada', color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
    in_progress: { label: 'En curso', color: '#8B5CF6', bg: 'rgba(139,92,246,0.15)' },
    in_review: { label: 'En revisión', color: '#EAB308', bg: 'rgba(234,179,8,0.15)' },
    approved: { label: 'Aprobada', color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
    rejected: { label: 'Rechazada', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
};

const FILTERS = [
    { key: 'all', label: 'Todos' },
    { key: 'pending', label: 'Pendiente' },
    { key: 'in_progress', label: 'En curso' },
    { key: 'in_review', label: 'En revisión' },
    { key: 'approved', label: 'Aprobada' },
    { key: 'rejected', label: 'Rechazada' },
];

function todayLabel() {
    const d = new Date();
    return d.toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' });
}

function shortDate(iso) {
    if (!iso) return '—';
    // TZ-safe: treat "YYYY-MM-DD" as local so it isn't shifted to the previous
    // day in negative TZ offsets (Mexico City et al.).
    const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso);
    return d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AdminInspections() {
    const [requests, setRequests] = useState([]);
    const [standards, setStandards] = useState([]);
    const [distintivos, setDistintivos] = useState([]);
    const [clients, setClients] = useState([]);
    const [inspectors, setInspectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('table');
    // Filtro inicial desde ?filter= (KPIs del Dashboard Admin)
    const initialFilter = (typeof window !== 'undefined')
        ? (new URLSearchParams(window.location.search).get('filter') || 'all')
        : 'all';
    const [filter, setFilter] = useState(initialFilter);
    const [openId, setOpenId] = useState(null);
    const [newInspectionDate, setNewInspectionDate] = useState(null); // YYYY-MM-DD para modal de alta desde calendario
    const [preselectClientId, setPreselectClientId] = useState(null); // Iter 43 · from ?openNew=1&clientId=…
    // Iter 43 · toggle to include soft-deleted audits in the list ("Ver eliminadas").
    const [showDeleted, setShowDeleted] = useState(false);
    // Iter 43 · pending soft-delete confirmation (holds the enriched request row).
    const [pendingDelete, setPendingDelete] = useState(null);
    // Iter 44 · pending HARD-delete confirmation (only for already soft-deleted rows).
    const [pendingHardDelete, setPendingHardDelete] = useState(null);

    const handleRestore = async (r) => {
        try {
            await api.post(`/admin/inspections/${r.id}/restore`);
            toast.success('Auditoría restaurada');
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al restaurar auditoría'));
        }
    };

    const load = () => Promise.all([
        api.get(`/certifications${showDeleted ? '?include_deleted=true' : ''}`).then((r) => setRequests(r.data)),
        api.get('/standards').then((r) => setStandards(r.data)),
        api.get('/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => {}),
        api.get('/admin/users?role=client').then((r) => setClients(r.data)),
        api.get('/admin/users?role=inspector').then((r) => setInspectors(r.data)),
    ]).finally(() => setLoading(false));

    useEffect(() => { load(); }, [showDeleted]);

    // Iter 43 · shortcut from ClientDetail ("+ Nueva auditoría"): reading
    // ?openNew=1&clientId=… opens the modal for today with the client
    // preselected. Cleans the URL after so refreshing doesn't reopen it.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const p = new URLSearchParams(window.location.search);
        if (p.get('openNew') === '1') {
            const cid = p.get('clientId');
            if (cid) setPreselectClientId(cid);
            const today = new Date();
            const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            setNewInspectionDate(iso);
            // Strip the params so future navigation is clean.
            const url = new URL(window.location.href);
            url.searchParams.delete('openNew');
            url.searchParams.delete('clientId');
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    const stdLookup = useMemo(() => Object.fromEntries(standards.map((s) => [s.id, s])), [standards]);
    const distLookup = useMemo(() => Object.fromEntries((distintivos || []).map((d) => [d.id, d])), [distintivos]);
    const clientLookup = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const inspectorLookup = useMemo(() => Object.fromEntries(inspectors.map((i) => [i.id, i])), [inspectors]);

    // Iter 76 (corrección) · el distintivo (no la norma legal) es el único
    // identificador visible — nunca cae al código de norma, ni como fallback.
    const distintivoLabel = (r) => {
        const codes = (r.desired_distintivo_ids || []).map((id) => distLookup[id]?.code).filter(Boolean);
        return codes.length > 0 ? codes.join(' + ') : 'Distintivo';
    };

    const enriched = requests.map((r) => ({
        ...r,
        client: clientLookup[r.client_id],
        standard: stdLookup[r.standard_id],
        inspector: inspectorLookup[r.inspector_id],
        distintivoLabel: distintivoLabel(r),
    }));

    const filtered = enriched.filter((r) => {
        if (filter === 'all') return true;
        if (filter === 'in_progress') return ['assigned', 'in_progress'].includes(r.status);
        if (filter === 'pending_all') return ['pending', 'assigned', 'in_progress', 'in_review'].includes(r.status);
        if (filter === 'due_this_week') {
            // Iter 48 · Dashboard badge target: assigned/in_progress with a
            // scheduled_date that is overdue OR within the next 7 days.
            // Audits without a scheduled_date are excluded — they belong to
            // "in_progress" filter (admin's programming backlog), not urgency.
            if (!['assigned', 'in_progress'].includes(r.status)) return false;
            if (!r.scheduled_date) return false;
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const in7 = new Date(today); in7.setDate(in7.getDate() + 7);
            const d = new Date(r.scheduled_date);
            return d <= in7;
        }
        if (filter === 'completed_month') {
            if (!['approved', 'rejected'].includes(r.status)) return false;
            const decidedAt = r.admin_decided_at || r.updated_at;
            if (!decidedAt) return false;
            const d = new Date(decidedAt);
            const now = new Date();
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        }
        return r.status === filter;
    });

    const counts = FILTERS.reduce((acc, f) => {
        if (f.key === 'all') acc[f.key] = enriched.length;
        else if (f.key === 'in_progress') acc[f.key] = enriched.filter((r) => ['assigned', 'in_progress'].includes(r.status)).length;
        else acc[f.key] = enriched.filter((r) => r.status === f.key).length;
        return acc;
    }, {});

    return (
        <AdminLayout title="Auditorías">
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1 text-gray-500">Panel Admin</div>
                    <h1 className="font-display text-3xl font-black tracking-tight">Auditorías</h1>
                    <div className="text-xs text-gray-500 mt-1">{todayLabel()}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Iter 43 · Toggle "Ver eliminadas" — includes soft-deleted audits in the list. */}
                    <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer select-none"
                        title="Mostrar auditorías eliminadas (soft-delete)">
                        <input type="checkbox" checked={showDeleted}
                            onChange={(e) => setShowDeleted(e.target.checked)}
                            data-testid="toggle-show-deleted-audits"
                            className="w-4 h-4" />
                        Ver eliminadas
                    </label>
                    <div className="flex bg-white dark:bg-black/20 border" style={{ borderColor: 'var(--border)' }}>
                        <button data-testid="view-table" onClick={() => setView('table')}
                            className="px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
                            style={view === 'table' ? { background: ACCENT, color: 'white' } : {}}>
                            <TableIcon className="w-4 h-4" /> Tabla
                        </button>
                        <button data-testid="view-calendar" onClick={() => setView('calendar')}
                            className="px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
                            style={view === 'calendar' ? { background: ACCENT, color: 'white' } : {}}>
                            <CalendarIcon className="w-4 h-4" /> Cronograma
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter chips */}
            <div className="flex gap-2 mb-6 flex-wrap">
                {FILTERS.map((f) => (
                    <button key={f.key} data-testid={`filter-${f.key}`}
                        onClick={() => setFilter(f.key)}
                        className="px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                        style={{
                            background: filter === f.key ? ACCENT : 'var(--card-bg)',
                            color: filter === f.key ? 'white' : 'inherit',
                            border: `1px solid ${filter === f.key ? ACCENT : 'var(--border)'}`,
                        }}>
                        {f.label}
                        <span className="text-[10px] px-1.5 py-0.5" style={{
                            background: filter === f.key ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.06)',
                        }}>{counts[f.key] || 0}</span>
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="p-16 text-center text-gray-500">Cargando...</div>
            ) : view === 'table' ? (
                <TableView items={filtered} onOpen={setOpenId} onAskDelete={setPendingDelete} onAskHardDelete={setPendingHardDelete} onRestore={handleRestore} />
            ) : (
                <CalendarView items={enriched} onOpen={setOpenId} onNewOnDate={setNewInspectionDate} />
            )}

            {openId && (
                <ActionDrawer id={openId} onClose={() => setOpenId(null)} onReload={load}
                    detail={enriched.find((r) => r.id === openId)}
                    inspectors={inspectors} standards={standards} />
            )}

            {newInspectionDate && (
                <NewInspectionModal
                    date={newInspectionDate}
                    clients={clients}
                    inspectors={inspectors}
                    standards={standards}
                    distintivos={distintivos}
                    initialClientId={preselectClientId}
                    onClose={() => { setNewInspectionDate(null); setPreselectClientId(null); }}
                    onCreated={() => { setNewInspectionDate(null); setPreselectClientId(null); load(); }}
                />
            )}

            {pendingDelete && (
                <ConfirmDeleteInspectionModal
                    detail={pendingDelete}
                    onClose={() => setPendingDelete(null)}
                    onConfirmed={() => { setPendingDelete(null); load(); }}
                />
            )}

            {pendingHardDelete && (
                <ConfirmHardDeleteInspectionModal
                    detail={pendingHardDelete}
                    onClose={() => setPendingHardDelete(null)}
                    onConfirmed={() => { setPendingHardDelete(null); load(); }}
                />
            )}
        </AdminLayout>
    );
}

function TableView({ items, onOpen, onAskDelete, onAskHardDelete, onRestore }) {
    if (items.length === 0) {
        return (
            <div className="p-16 text-center text-gray-500" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No hay auditorías para este filtro.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="inspections-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--border)' }}>
                        <Th>Cliente / Servicio</Th>
                        <Th>Distintivo</Th>
                        <Th>Auditor</Th>
                        <Th>Fecha</Th>
                        <Th>Estado</Th>
                        <Th align="right">Acciones</Th>
                    </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                    {items.map((r) => (
                        <tr key={r.id}
                            className={`hover:bg-black/[0.03] dark:hover:bg-white/[0.03] ${r.deleted ? 'opacity-60 italic' : ''}`}
                            data-testid={`row-${r.id}`}>
                            <td className="px-5 py-4">
                                <Link to={r.client ? `/admin/clients/${r.client.id}` : '#'} className="font-bold hover:underline">
                                    {r.client?.company || r.client?.trade_name || r.client?.name || '—'}
                                </Link>
                                {r.deleted && (
                                    <span className="ml-2 inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-red-100 text-red-700 not-italic">
                                        eliminada
                                    </span>
                                )}
                                <div className="text-[11px] text-gray-500 uppercase tracking-wider mt-0.5">
                                    {r.client?.industry || 'Sin giro'}
                                </div>
                            </td>
                            <td className="px-5 py-4">
                                <div className="font-mono text-xs font-bold">{r.distintivoLabel || '—'}</div>
                            </td>
                            <td className="px-5 py-4">
                                {r.inspector ? (
                                    <div className="flex items-center gap-2 min-w-0">
                                        {r.inspector.photo_url ? (
                                            <img src={r.inspector.photo_url} alt={r.inspector.name} className="w-8 h-8 object-cover shrink-0" />
                                        ) : (
                                            <div className="w-8 h-8 flex items-center justify-center text-xs font-bold text-white shrink-0"
                                                style={{ background: '#00875A' }}>
                                                {(r.inspector.name || 'I').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-xs font-bold truncate">{r.inspector.name}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-xs text-gray-400 italic">Sin asignar</span>
                                )}
                            </td>
                            <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">{shortDate(r.scheduled_date || r.created_at)}</td>
                            <td className="px-5 py-4">
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                    style={{ background: STATUS_META[r.status]?.bg, color: STATUS_META[r.status]?.color }}>
                                    {STATUS_META[r.status]?.label || r.status}
                                </span>
                            </td>
                            <td className="px-5 py-4 text-right">
                                <div className="inline-flex gap-2">
                                    <Link to={`/admin/inspections/${r.id}/form`} data-testid={`view-form-${r.id}`}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white hover:opacity-90"
                                        style={{ background: ACCENT }}>
                                        <FileText className="w-3 h-3" /> Ver formulario
                                    </Link>
                                    <button onClick={() => onOpen(r.id)} data-testid={`open-actions-${r.id}`}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border hover:bg-black/5 dark:hover:bg-white/5"
                                        style={{ borderColor: 'var(--border)' }}>
                                        Gestionar
                                    </button>
                                    {r.deleted ? (
                                        <>
                                            <button onClick={() => onRestore(r)}
                                                data-testid={`restore-audit-${r.id}`}
                                                title="Restaurar auditoría"
                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors">
                                                <Undo2 className="w-3 h-3" /> Restaurar
                                            </button>
                                            <button onClick={() => onAskHardDelete(r)}
                                                data-testid={`hard-delete-audit-${r.id}`}
                                                title="Eliminar definitivamente (permanente)"
                                                className="inline-flex items-center justify-center px-2 py-1.5 text-white bg-red-700 hover:bg-red-800 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </>
                                    ) : (
                                        <button onClick={() => onAskDelete(r)}
                                            data-testid={`delete-audit-${r.id}`}
                                            title="Eliminar auditoría"
                                            className="inline-flex items-center justify-center px-2 py-1.5 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ConfirmDeleteInspectionModal({ detail, onClose, onConfirmed }) {
    const [busy, setBusy] = useState(false);
    const doDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/inspections/${detail.id}`);
            toast.success('Auditoría eliminada (podrás restaurarla desde "Ver eliminadas")');
            onConfirmed();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al eliminar auditoría'));
        } finally {
            setBusy(false);
        }
    };
    const clientLabel = detail.client?.company || detail.client?.trade_name || detail.client?.name || '—';
    const dateLabel = shortDate(detail.scheduled_date || detail.created_at);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-inspection-modal">
            <div className="absolute inset-0 bg-black/60" onClick={busy ? undefined : onClose} />
            <div className="relative w-full max-w-md shadow-2xl" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: 'var(--border)' }}>
                    <AlertCircle className="w-6 h-6 text-red-600" />
                    <div className="font-display font-black text-lg">Eliminar auditoría</div>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    <div className="text-gray-600 dark:text-gray-300">
                        Esta acción marca la auditoría como eliminada. Podrás recuperarla desde el toggle
                        <strong> &quot;Ver eliminadas&quot;</strong> en cualquier momento — las respuestas del auditor, evidencias
                        y feedback del admin se conservan intactos.
                    </div>
                    <div className="border p-3 space-y-1" style={{ borderColor: 'var(--border)' }}>
                        <Row label="Cliente" value={clientLabel} />
                        <Row label="Distintivo" value={detail.distintivoLabel} />
                        <Row label="Fecha" value={dateLabel} />
                    </div>
                </div>
                <div className="p-5 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose} disabled={busy}
                        data-testid="confirm-delete-inspection-cancel"
                        className="px-4 py-2 text-sm font-bold border hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                    <button onClick={doDelete} disabled={busy}
                        data-testid="confirm-delete-inspection-submit"
                        className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Eliminar auditoría
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline gap-3 text-xs">
            <div className="uppercase tracking-widest font-bold text-gray-500 w-16 shrink-0">{label}</div>
            <div className="font-medium truncate">{value}</div>
        </div>
    );
}

/**
 * Iter 44 · Hard-delete confirmation for an already-soft-deleted audit.
 * Requires typing the exact distintivo code (or "ELIMINAR PERMANENTEMENTE" as
 * fallback) — the intent is friction on top of friction, since the record is
 * being erased from the DB forever with no restore path.
 * Iter 76 (corrección) · usa el código del Distintivo, nunca la norma legal.
 */
function ConfirmHardDeleteInspectionModal({ detail, onClose, onConfirmed }) {
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const clientLabel = detail.client?.company || detail.client?.trade_name || detail.client?.name || '—';
    const distCode = detail.distintivoLabel || '';
    const dateLabel = shortDate(detail.scheduled_date || detail.created_at);
    // Accepted confirmations: distintivo code OR the sentinel string.
    const sentinel = 'ELIMINAR PERMANENTEMENTE';
    const confirmMatches = (distCode && distCode !== '—' && confirmText.trim().toUpperCase() === distCode.toUpperCase())
        || confirmText.trim().toUpperCase() === sentinel;

    const doHardDelete = async () => {
        if (!confirmMatches) return;
        setBusy(true);
        try {
            await api.delete(`/admin/inspections/${detail.id}/permanent`);
            toast.success('Auditoría eliminada permanentemente');
            onConfirmed();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al eliminar permanentemente'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="confirm-hard-delete-inspection-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
            <div className="relative w-full max-w-md shadow-2xl" style={{ background: 'var(--card-bg)', border: '2px solid #991B1B' }}>
                <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: '#991B1B', background: 'rgba(153,27,27,0.08)' }}>
                    <AlertCircle className="w-6 h-6 text-red-700" />
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-red-700">Acción irreversible</div>
                        <div className="font-display font-black text-lg">Eliminar auditoría permanentemente</div>
                    </div>
                </div>
                <div className="p-5 space-y-4 text-sm">
                    <div className="text-gray-700 dark:text-gray-200">
                        Estás a punto de <strong>borrar de la base de datos</strong> esta auditoría.
                        Esta acción es <strong className="text-red-700">definitiva y no se puede deshacer</strong>.
                        Las respuestas del auditor, evidencias, feedback del admin y planes de acción
                        quedarán huérfanos (no se borran en cascada — consistente con Iter 43).
                    </div>
                    <div className="border p-3 space-y-1" style={{ borderColor: 'var(--border)' }}>
                        <Row label="Cliente" value={clientLabel} />
                        <Row label="Distintivo" value={detail.distintivoLabel} />
                        <Row label="Fecha" value={dateLabel} />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">
                            Escribe <span className="text-red-700 font-mono">{distCode || sentinel}</span> para confirmar:
                        </label>
                        <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={distCode || sentinel}
                            data-testid="confirm-hard-delete-inspection-input"
                            autoFocus
                            className="w-full px-3 py-2 border-2 outline-none font-mono text-sm"
                            style={{ borderColor: confirmMatches ? '#10B981' : 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                </div>
                <div className="p-5 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose} disabled={busy}
                        data-testid="confirm-hard-delete-inspection-cancel"
                        className="px-4 py-2 text-sm font-bold border hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                    <button onClick={doHardDelete} disabled={!confirmMatches || busy}
                        data-testid="confirm-hard-delete-inspection-submit"
                        className="px-4 py-2 text-sm font-bold text-white bg-red-800 hover:bg-red-900 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Eliminar para siempre
                    </button>
                </div>
            </div>
        </div>
    );
}

function Th({ children, align }) {
    return <th className="text-left px-5 py-3 font-bold text-[11px] uppercase tracking-[0.15em] text-gray-500"
        style={{ textAlign: align || 'left' }}>{children}</th>;
}

function CalendarView({ items, onOpen, onNewOnDate }) {
    const [cursor, setCursor] = useState(new Date());
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const startDay = (first.getDay() + 6) % 7; // Monday-first (0=Mon)

    const byDay = useMemo(() => {
        const map = {};
        items.forEach((r) => {
            const d = r.scheduled_date || r.created_at;
            if (!d) return;
            // TZ-safe parse: date-only strings ("YYYY-MM-DD") must be treated
            // as local dates. `new Date("2026-07-15")` parses as UTC midnight,
            // so `.getDate()` returns the previous day in negative TZ offsets
            // (e.g. Mexico City → 14). Detect the date-only form and construct
            // a local Date explicitly.
            let dt;
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
                const [yy, mm, dd] = d.split('-').map(Number);
                dt = new Date(yy, mm - 1, dd);
            } else {
                dt = new Date(d);
            }
            if (dt.getFullYear() === y && dt.getMonth() === m) {
                const day = dt.getDate();
                (map[day] = map[day] || []).push(r);
            }
        });
        return map;
    }, [items, y, m]);

    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    while (days.length % 7 !== 0) days.push(null);

    const monthLabel = cursor.toLocaleDateString('es', { month: 'long', year: 'numeric' });

    const openNew = (day) => {
        const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onNewOnDate?.(iso);
    };

    return (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="inspections-calendar">
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/5" data-testid="cal-prev">
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="font-display text-lg font-black capitalize">{monthLabel}</div>
                <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/5" data-testid="cal-next">
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-7 border-b" style={{ borderColor: 'var(--border)' }}>
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
                    <div key={d} className="text-center text-[10px] uppercase tracking-wider font-bold text-gray-500 py-2">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {days.map((d, idx) => (
                    <div key={idx}
                        onClick={d ? () => openNew(d) : undefined}
                        data-testid={d ? `cal-day-${d}` : undefined}
                        className={`min-h-[110px] p-2 border-r border-b transition-colors relative group ${d ? 'cursor-pointer hover:bg-[rgba(124,107,239,0.06)]' : ''}`}
                        style={{ borderColor: 'var(--border)', background: d ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                        {d && (
                            <>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-bold text-gray-500">{d}</span>
                                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Plus className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                                    </span>
                                </div>
                                {(byDay[d] || []).slice(0, 3).map((r) => (
                                    <button key={r.id}
                                        onClick={(e) => { e.stopPropagation(); onOpen(r.id); }}
                                        data-testid={`cal-event-${r.id}`}
                                        className="block w-full text-left text-[10px] px-1.5 py-1 mb-1 truncate hover:opacity-90"
                                        style={{ background: STATUS_META[r.status]?.bg, color: STATUS_META[r.status]?.color, borderLeft: `2px solid ${STATUS_META[r.status]?.color}` }}
                                        title={`${r.client?.company || r.client?.name} · ${r.distintivoLabel}`}>
                                        {r.client?.company || r.client?.name || 'Cliente'} · {r.distintivoLabel}
                                    </button>
                                ))}
                                {(byDay[d] || []).length > 3 && (
                                    <div className="text-[10px] text-gray-400 italic">+{byDay[d].length - 3} más</div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>
            <div className="px-5 py-3 border-t text-[11px] opacity-60" style={{ borderColor: 'var(--border)' }}>
                Tip: haz clic en cualquier casilla del calendario para agendar una nueva auditoría en esa fecha.
            </div>
        </div>
    );
}

function NewInspectionModal({ date, clients, inspectors, standards, distintivos, initialClientId, onClose, onCreated }) {
    const [form, setForm] = useState({
        client_id: '',
        industry: '',
        inspector_id: '',
        scheduled_time: '09:00',
        notes: '',
        desired_distintivo_ids: [],   // Iter 63bis · replaces the old `standard_id`
    });
    // Iter 72 · Fecha ahora es editable (antes quedaba fija al día del
    // calendario en el que se hizo clic, sin selector visual funcional).
    const [scheduledDate, setScheduledDate] = useState(date);
    const [busy, setBusy] = useState(false);

    // Iter 63bis · only distintivos that are simultaneously active, not
    // coming_soon, and have a default_standard_id are actually pickable.
    // Others render disabled with a hint badge so admins understand why.
    const selectableDistintivos = (distintivos || []).filter((d) => d.is_active !== false);
    const toggleDistintivo = (id) => {
        setForm((f) => {
            const has = f.desired_distintivo_ids.includes(id);
            return {
                ...f,
                desired_distintivo_ids: has
                    ? f.desired_distintivo_ids.filter((x) => x !== id)
                    : [...f.desired_distintivo_ids, id],
            };
        });
    };

    // Auto-fill industry when client is selected
    const onClientChange = (cid) => {
        const c = clients.find((x) => x.id === cid);
        setForm((f) => ({ ...f, client_id: cid, industry: c?.industry || f.industry }));
    };

    // Preselect client (Iter 43): if `initialClientId` is passed (from the
    // "+ Nueva auditoría" shortcut on the client profile), populate it once
    // the clients list is available and derive the industry.
    useEffect(() => {
        if (!initialClientId) return;
        if (form.client_id) return;
        if (!clients?.length) return;
        const c = clients.find((x) => x.id === initialClientId);
        if (c) {
            setForm((f) => ({ ...f, client_id: c.id, industry: c.industry || f.industry }));
        }
    }, [initialClientId, clients, form.client_id]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.client_id) { toast.error('Selecciona un cliente'); return; }
        if (!form.desired_distintivo_ids.length) { toast.error('Selecciona al menos un distintivo'); return; }
        if (!form.inspector_id) { toast.error('Asigna un auditor — no se puede crear una auditoría sin auditor asignado'); return; }
        if (!scheduledDate) { toast.error('Selecciona una fecha'); return; }
        setBusy(true);
        try {
            await api.post('/admin/inspections', {
                client_id: form.client_id,
                // Iter 63bis · standard_id se deriva del primer distintivo (backend hace el mapeo)
                desired_distintivo_ids: form.desired_distintivo_ids,
                industry: form.industry || undefined,
                inspector_id: form.inspector_id,
                scheduled_date: scheduledDate,
                scheduled_time: form.scheduled_time || undefined,
                notes: form.notes || '',
            });
            toast.success('Auditoría agendada');
            onCreated();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al crear auditoría'));
        } finally {
            setBusy(false);
        }
    };

    const prettyDate = new Date(`${scheduledDate}T00:00:00`).toLocaleDateString('es', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-inspection-modal">
            <div className="absolute inset-0 bg-black/60" onClick={busy ? undefined : onClose} />
            <div className="relative w-full max-w-lg shadow-2xl flex flex-col max-h-[calc(100vh-2rem)]" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-center justify-between gap-3 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: ACCENT }}>Nueva auditoría</div>
                        <div className="font-display font-black text-lg capitalize">{prettyDate}</div>
                    </div>
                    <button onClick={onClose} disabled={busy} className="p-2 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50" data-testid="new-inspection-close">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
                <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
                    {/* Cliente */}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">Cliente *</label>
                        <select value={form.client_id}
                            onChange={(e) => onClientChange(e.target.value)}
                            required
                            data-testid="new-insp-client"
                            className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                            style={{ borderColor: 'var(--border)' }}>
                            <option value="">— Selecciona un cliente —</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>{c.company || c.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Giro / Rubro */}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">Giro de la Empresa · Rubro</label>
                        <input type="text" value={form.industry}
                            onChange={(e) => setForm({ ...form, industry: e.target.value })}
                            placeholder="Se autocompleta con el rubro del cliente"
                            data-testid="new-insp-industry"
                            className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                            style={{ borderColor: 'var(--border)' }} />
                    </div>

                    {/* Iter 63bis · Distintivos comerciales (reemplaza al select de Norma) */}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">
                            Distintivos a auditar * <span className="opacity-60 font-normal">(1 o más)</span>
                        </label>
                        {(!distintivos || distintivos.length === 0) ? (
                            <div className="text-xs italic opacity-60 border px-3 py-2" style={{ borderColor: 'var(--border)' }}>
                                Cargando distintivos…
                            </div>
                        ) : (
                            <div className="border max-h-56 overflow-y-auto" style={{ borderColor: 'var(--border)' }} data-testid="new-insp-distintivos-list">
                                {selectableDistintivos.map((d) => {
                                    const isComingSoon = !!d.coming_soon;
                                    const disabled = isComingSoon;
                                    const checked = form.desired_distintivo_ids.includes(d.id);
                                    return (
                                        <label
                                            key={d.id}
                                            className={`flex items-start gap-2 px-3 py-2 border-b last:border-b-0 text-sm ${disabled ? 'opacity-55 cursor-not-allowed' : 'cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/5'}`}
                                            style={{ borderColor: 'var(--border)' }}
                                            title={isComingSoon ? 'Estará disponible próximamente' : ''}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={disabled}
                                                onChange={() => !disabled && toggleDistintivo(d.id)}
                                                data-testid={`new-insp-distintivo-${d.code}`}
                                                className="mt-0.5"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-bold text-xs">{d.code}</span>
                                                    {isComingSoon && (
                                                        <span
                                                            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                                                            style={{ background: 'rgba(245,158,11,0.2)', color: '#B45309' }}
                                                            data-testid={`coming-soon-badge-${d.code}`}
                                                        >
                                                            Próximamente
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] opacity-60 truncate">{d.name}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                                {selectableDistintivos.length === 0 && (
                                    <div className="px-3 py-4 text-xs italic opacity-60">
                                        No hay distintivos activos disponibles.
                                    </div>
                                )}
                            </div>
                        )}
                        <p className="text-[10px] opacity-60 mt-1.5">
                            El checklist del auditor se filtra automáticamente al alcance de estos distintivos.
                            La norma legal se deriva del primer distintivo seleccionado.
                        </p>
                    </div>

                    {/* Inspector */}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">Auditor asignado *</label>
                        <select value={form.inspector_id}
                            onChange={(e) => setForm({ ...form, inspector_id: e.target.value })}
                            required
                            data-testid="new-insp-inspector"
                            className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                            style={{ borderColor: 'var(--border)' }}>
                            <option value="">— Sin asignar aún —</option>
                            {inspectors.map((i) => (
                                <option key={i.id} value={i.id}>{i.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Fecha (editable — Iter 72) + Hora */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">Fecha</label>
                            <input type="date" value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                required
                                data-testid="new-insp-date"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">Hora</label>
                            <input type="time" value={form.scheduled_time}
                                onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                                data-testid="new-insp-time"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                    </div>

                    {/* Observaciones */}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">Observaciones</label>
                        <textarea rows={3} value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Notas internas, indicaciones especiales..."
                            data-testid="new-insp-notes"
                            className="w-full px-3 py-2 border outline-none text-sm resize-none bg-transparent"
                            style={{ borderColor: 'var(--border)' }} />
                    </div>
                </div>

                    {/* Actions — sticky footer, always reachable regardless of body length */}
                    <div className="flex gap-2 p-5 pt-3 border-t shrink-0" style={{ borderColor: 'var(--border)' }}>
                        <button type="submit" disabled={busy || !form.inspector_id}
                            data-testid="new-insp-create"
                            title={!form.inspector_id ? 'Asigna un auditor para poder crear la auditoría' : ''}
                            className="flex-1 text-white px-5 py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            style={{ background: ACCENT }}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Crear auditoría
                        </button>
                        <button type="button" onClick={onClose} disabled={busy}
                            data-testid="new-insp-cancel"
                            className="border-2 px-5 py-3 font-bold hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                            style={{ borderColor: 'var(--border)' }}>
                            Cancelar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ActionDrawer({ id, detail, onClose, onReload, inspectors, standards }) {
    const [full, setFull] = useState(null);
    const [assignSel, setAssignSel] = useState('');
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState('');
    const [editForm, setEditForm] = useState({ scheduled_date: '', scheduled_time: '', notes: '' });
    const [editing, setEditing] = useState(false);
    const [verdict, setVerdict] = useState(null);
    const [verdictLoading, setVerdictLoading] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [overrideDecision, setOverrideDecision] = useState('approved');
    const [overrideReason, setOverrideReason] = useState('');

    const loadVerdict = async () => {
        setVerdictLoading(true);
        try {
            const r = await api.get(`/certifications/${id}/verdict`);
            setVerdict(r.data);
        } catch (e) { /* silencioso: el panel muestra estado de carga */ }
        finally { setVerdictLoading(false); }
    };

    useEffect(() => {
        api.get(`/certifications/${id}`).then((r) => {
            setFull(r.data);
            setAssignSel(r.data.request.inspector_id || '');
            setEditForm({
                scheduled_date: r.data.request.scheduled_date || '',
                scheduled_time: r.data.request.scheduled_time || '',
                notes: r.data.request.notes || '',
            });
            if (['in_review', 'approved', 'rejected'].includes(r.data.request.status)) loadVerdict();
        });
    }, [id]);

    const assign = async () => {
        if (!assignSel) { toast.error('Selecciona un auditor'); return; }
        setBusy('assign');
        try {
            await api.patch(`/certifications/${id}/assign`, { inspector_id: assignSel });
            toast.success('Auditor asignado — se envió notificación');
            await onReload(); onClose();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al asignar')); }
        finally { setBusy(''); }
    };

    const saveEdit = async () => {
        setBusy('edit');
        try {
            const r = await api.patch(`/admin/inspections/${id}`, editForm);
            if (r.data.changed) {
                toast.success('Auditoría actualizada — se envió notificación al auditor');
            } else {
                toast.info('No hubo cambios');
            }
            setEditing(false);
            await onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al actualizar')); }
        finally { setBusy(''); }
    };

    const generate = async () => {
        setBusy('gen');
        try {
            await api.post('/checklists/generate', { request_id: id, extra_context: '' });
            toast.success('Checklist generado por IA');
            await onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al generar')); }
        finally { setBusy(''); }
    };

    const confirmDictamen = async () => {
        setBusy('confirm');
        try {
            const r = await api.post(`/certifications/${id}/decision`, { comment });
            const dec = r.data?.decision;
            toast.success(`Dictamen fijado: ${dec === 'approved' ? 'APROBADA' : 'RECHAZADA'}. Aún NO se ha enviado al cliente.`);
            const rr = await api.get(`/certifications/${id}`); setFull(rr.data);
            await onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al fijar el dictamen')); }
        finally { setBusy(''); }
    };

    const overrideDictamen = async () => {
        if (overrideReason.trim().length < 10) {
            toast.error('El motivo de la anulación es obligatorio (mínimo 10 caracteres).');
            return;
        }
        setBusy('override');
        try {
            const r = await api.post(`/certifications/${id}/override`, { final_decision: overrideDecision, reason: overrideReason.trim() });
            toast.success(`Anulación aplicada: ${r.data?.decision === 'approved' ? 'APROBADA' : 'RECHAZADA'} (cálculo era ${r.data?.computed_decision === 'approved' ? 'APROBADA' : 'RECHAZADA'}). No se ha enviado al cliente.`);
            setOverrideOpen(false); setOverrideReason('');
            const rr = await api.get(`/certifications/${id}`); setFull(rr.data);
            await onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al anular')); }
        finally { setBusy(''); }
    };

    const sendToClient = async () => {
        if (!window.confirm('Se enviará el dictamen al cliente por correo (con el PDF) y una notificación en la app. Esta acción no se puede deshacer. ¿Continuar?')) return;
        setBusy('send');
        try {
            await api.post(`/certifications/${id}/send-result`);
            toast.success('Dictamen enviado al cliente.');
            const rr = await api.get(`/certifications/${id}`); setFull(rr.data);
            await onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'No se pudo enviar')); }
        finally { setBusy(''); }
    };

    const preview = async () => {
        // Abrir la pestaña DENTRO del gesto de clic evita que el navegador la
        // bloquee (el await posterior rompería el "user gesture").
        const win = window.open('', '_blank');
        setBusy('preview');
        try {
            const res = await api.get(`/certifications/${id}/report.pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            if (win) win.location.href = url; else window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            if (win) win.close();
            toast.error(apiErrorMessage(e, 'No se pudo generar el reporte.'));
        }
        finally { setBusy(''); }
    };

    const download = async () => {
        try {
            const res = await api.get(`/certifications/${id}/report.pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.download = `reporte-${id.slice(0, 8)}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error('Sin reporte aún'); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} data-testid="action-drawer"
                className="w-full max-w-md h-full overflow-y-auto"
                style={{ background: 'var(--card-bg)', borderLeft: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Solicitud</div>
                        <div className="font-display font-black text-lg">{id.slice(0, 8)}</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5"><XIcon className="w-5 h-5" /></button>
                </div>

                {!full ? <div className="p-8 text-center text-gray-500">Cargando...</div> : (
                    <div className="p-5 space-y-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Estado</div>
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                style={{ background: STATUS_META[full.request.status]?.bg, color: STATUS_META[full.request.status]?.color }}>
                                {STATUS_META[full.request.status]?.label}
                            </span>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Cliente / Empresa</div>
                            <div className="text-sm font-bold">{detail?.client?.company || detail?.client?.name || '—'}</div>
                            <div className="text-xs text-gray-500">{detail?.client?.industry}</div>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Distintivo</div>
                            <div className="text-sm font-mono font-bold">{detail?.distintivoLabel || '—'}</div>
                        </div>

                        {['pending', 'assigned'].includes(full.request.status) && (
                            <div>
                                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Asignar auditor</div>
                                <select value={assignSel} onChange={(e) => setAssignSel(e.target.value)}
                                    data-testid="drawer-inspector-select"
                                    className="w-full px-3 py-2 border outline-none text-sm mb-2"
                                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                                    <option value="">— Selecciona —</option>
                                    {inspectors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                                </select>
                                <button onClick={assign} disabled={busy === 'assign'} data-testid="drawer-assign-btn"
                                    className="w-full px-3 py-2 text-sm font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                    style={{ background: ACCENT }}>
                                    {busy === 'assign' ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />} Asignar
                                </button>
                            </div>
                        )}

                        {full.request.status === 'pending' && !full.request.checklist_id && (
                            <button onClick={generate} disabled={busy === 'gen'}
                                className="w-full px-3 py-2 text-sm font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                style={{ background: '#00875A' }}>
                                {busy === 'gen' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar checklist IA
                            </button>
                        )}

                        <Link to={`/admin/inspections/${id}/form`}
                            className="block w-full text-center px-3 py-2 text-sm font-bold border hover:bg-black/5 dark:hover:bg-white/5"
                            style={{ borderColor: 'var(--border)' }}>
                            <FileText className="inline w-4 h-4 mr-1" /> Ver formulario completo
                        </Link>

                        {/* Editar programación / observaciones */}
                        {['pending', 'assigned', 'in_progress'].includes(full.request.status) && (
                            <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Programación y notas</div>
                                    <button onClick={() => setEditing(!editing)}
                                        data-testid="drawer-edit-toggle"
                                        className="text-[11px] font-bold hover:underline" style={{ color: ACCENT }}>
                                        {editing ? 'Cancelar' : 'Editar'}
                                    </button>
                                </div>
                                {editing ? (
                                    <>
                                        <div className="grid grid-cols-2 gap-2 mb-2">
                                            <input type="date" value={editForm.scheduled_date}
                                                onChange={(e) => setEditForm({ ...editForm, scheduled_date: e.target.value })}
                                                data-testid="drawer-edit-date"
                                                className="px-3 py-2 border outline-none text-sm bg-transparent"
                                                style={{ borderColor: 'var(--border)' }} />
                                            <input type="time" value={editForm.scheduled_time}
                                                onChange={(e) => setEditForm({ ...editForm, scheduled_time: e.target.value })}
                                                data-testid="drawer-edit-time"
                                                className="px-3 py-2 border outline-none text-sm bg-transparent"
                                                style={{ borderColor: 'var(--border)' }} />
                                        </div>
                                        <textarea rows={3} value={editForm.notes}
                                            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                            data-testid="drawer-edit-notes"
                                            placeholder="Observaciones..."
                                            className="w-full px-3 py-2 border outline-none text-sm mb-2 resize-none bg-transparent"
                                            style={{ borderColor: 'var(--border)' }} />
                                        <button onClick={saveEdit} disabled={busy === 'edit'}
                                            data-testid="drawer-edit-save"
                                            className="w-full px-3 py-2 text-sm font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                            style={{ background: ACCENT }}>
                                            {busy === 'edit' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                            Guardar y notificar
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-xs space-y-1 opacity-80">
                                        <div>📅 {full.request.scheduled_date || '—'} · 🕐 {full.request.scheduled_time || '—'}</div>
                                        {full.request.notes && <div className="opacity-70">📝 {full.request.notes}</div>}
                                    </div>
                                )}
                            </div>
                        )}

                        {['in_review', 'approved', 'rejected'].includes(full.request.status) && (
                            <div className="border-t pt-4 space-y-4" style={{ borderColor: 'var(--border)' }}>
                                {/* DICTAMEN AUTOMÁTICO */}
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2 flex items-center gap-1">
                                        <Gavel className="w-3.5 h-3.5" /> Dictamen automático (reglas objetivas)
                                    </div>
                                    {verdictLoading || !verdict ? (
                                        <div className="text-xs text-gray-400 flex items-center gap-2 py-2">
                                            <Loader2 className="w-4 h-4 animate-spin" /> Calculando dictamen…
                                        </div>
                                    ) : (
                                        <div data-testid="verdict-card">
                                            {(() => {
                                                const rej = verdict.overall_decision === 'rejected';
                                                const c = rej ? '#EF4444' : '#10B981';
                                                const bg = rej ? 'rgba(239,68,68,0.10)' : 'rgba(16,185,129,0.10)';
                                                const Icon = rej ? ShieldAlert : ShieldCheck;
                                                return (
                                                    <div className="border p-3" style={{ borderColor: c, background: bg }}>
                                                        <div className="flex items-center gap-2 font-black text-base" style={{ color: c }} data-testid="verdict-badge">
                                                            <Icon className="w-5 h-5" /> {rej ? 'RECHAZADA' : 'APROBADA'}
                                                        </div>
                                                        <div className="mt-2 space-y-1.5">
                                                            {(verdict.per_distintivo || []).map((d, i) => (
                                                                <div key={i} className="text-xs">
                                                                    <span className="font-bold">{d.distintivo_name || d.distintivo_code || 'Auditoría'}:</span>{' '}
                                                                    <span style={{ color: d.score >= d.threshold ? '#10B981' : '#EF4444' }}>
                                                                        {d.score}% de cumplimiento
                                                                    </span>{' '}
                                                                    <span className="text-gray-500">· umbral requerido: {d.threshold}%</span>
                                                                    {d.blocked_by_critical && (
                                                                        <span className="ml-1 font-bold" style={{ color: '#EF4444' }}>
                                                                            · kill-switch activado ({d.blocking_failures_count} ítem{d.blocking_failures_count === 1 ? '' : 's'} crítico{d.blocking_failures_count === 1 ? '' : 's'})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {(verdict.blocking_failure_items || []).length > 0 && (
                                                            <div className="mt-2 pt-2 border-t" style={{ borderColor: 'rgba(239,68,68,0.25)' }} data-testid="verdict-critical-list">
                                                                <div className="text-[11px] font-bold" style={{ color: '#B91C1C' }}>Ítems críticos en “no cumple”:</div>
                                                                <ul className="mt-1 space-y-0.5">
                                                                    {(verdict.blocking_failure_items || []).map((bi) => (
                                                                        <li key={bi.id} className="text-[11px] text-gray-600 dark:text-gray-300">
                                                                            • <b className="font-mono">{bi.id}</b> — {bi.question}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>

                                {/* REPORTE: previsualizar / descargar (siempre disponible) */}
                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={preview} disabled={busy === 'preview'} data-testid="report-preview-btn"
                                        className="px-3 py-2 text-sm font-bold border inline-flex items-center justify-center gap-2 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                                        style={{ borderColor: ACCENT, color: ACCENT }}>
                                        {busy === 'preview' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Previsualizar
                                    </button>
                                    <button onClick={download} data-testid="report-download-btn"
                                        className="px-3 py-2 text-sm font-bold border inline-flex items-center justify-center gap-2 hover:bg-black/5 dark:hover:bg-white/5"
                                        style={{ borderColor: 'var(--border)' }}>
                                        <Download className="w-4 h-4" /> Descargar PDF
                                    </button>
                                </div>

                                {/* CONFIRMAR DICTAMEN (solo en revisión, no envía) */}
                                {full.request.status === 'in_review' && (
                                    <div className="space-y-2">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Comentario para el reporte (opcional)</div>
                                        <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                                            data-testid="dictamen-comment"
                                            className="w-full px-3 py-2 border outline-none text-sm resize-none"
                                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                                        <button onClick={confirmDictamen} disabled={busy === 'confirm' || !verdict} data-testid="dictamen-confirm-btn"
                                            className="w-full px-3 py-2.5 text-sm font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                            style={{ background: verdict?.overall_decision === 'rejected' ? '#EF4444' : '#10B981' }}>
                                            {busy === 'confirm' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />}
                                            Confirmar dictamen{verdict ? ` (${verdict.overall_decision === 'rejected' ? 'RECHAZADA' : 'APROBADA'})` : ''}
                                        </button>
                                        <p className="text-[11px] text-gray-500">Fija el resultado calculado. El cliente NO recibe nada todavía — el envío es un paso aparte.</p>

                                        {/* ANULACIÓN ADMINISTRATIVA (excepción) */}
                                        <div className="pt-1">
                                            <button type="button" onClick={() => setOverrideOpen(!overrideOpen)} data-testid="override-toggle"
                                                className="text-[11px] font-bold inline-flex items-center gap-1 hover:underline" style={{ color: '#B45309' }}>
                                                <AlertCircle className="w-3.5 h-3.5" /> {overrideOpen ? 'Cancelar anulación' : 'Anulación administrativa (excepción)'}
                                            </button>
                                            {overrideOpen && (
                                                <div className="mt-2 border p-3 space-y-2" style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.06)' }}>
                                                    <p className="text-[11px] text-gray-600 dark:text-gray-300">
                                                        Solo para casos justificados. Se registra quién la hizo, cuándo, el motivo y el resultado calculado vs. el final. El PDF mostrará un aviso de anulación.
                                                    </p>
                                                    <select value={overrideDecision} onChange={(e) => setOverrideDecision(e.target.value)} data-testid="override-decision"
                                                        className="w-full px-3 py-2 border outline-none text-sm bg-transparent" style={{ borderColor: 'var(--border)' }}>
                                                        <option value="approved">Forzar APROBADA</option>
                                                        <option value="rejected">Forzar RECHAZADA</option>
                                                    </select>
                                                    <textarea rows={2} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                                                        data-testid="override-reason" placeholder="Motivo de la anulación (obligatorio, mín. 10 caracteres)…"
                                                        className="w-full px-3 py-2 border outline-none text-sm resize-none" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                                                    <button onClick={overrideDictamen} disabled={busy === 'override'} data-testid="override-apply-btn"
                                                        className="w-full px-3 py-2 text-sm font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                                        style={{ background: '#B45309' }}>
                                                        {busy === 'override' ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />} Aplicar anulación
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ENVIAR AL CLIENTE (solo tras fijar el dictamen) */}
                                {['approved', 'rejected'].includes(full.request.status) && (
                                    <div className="space-y-2 border-t pt-3" style={{ borderColor: 'var(--border)' }}>
                                        {full.request.override?.active && (
                                            <div className="text-[11px] p-2 border" style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)', color: '#B45309' }}>
                                                <b>Dictamen por anulación administrativa.</b> Cálculo: {full.request.override.computed_decision === 'approved' ? 'APROBADA' : 'RECHAZADA'} → Final: {full.request.override.final_decision === 'approved' ? 'APROBADA' : 'RECHAZADA'}. Motivo: {full.request.override.reason}
                                            </div>
                                        )}
                                        {full.request.result_sent_at ? (
                                            <div className="text-[11px] font-bold" style={{ color: '#10B981' }} data-testid="result-sent-note">
                                                ✓ Enviado al cliente el {new Date(full.request.result_sent_at).toLocaleString('es-MX')}
                                            </div>
                                        ) : (
                                            <p className="text-[11px] text-amber-600">Dictamen fijado pero <b>aún no enviado</b> al cliente. Revisa el PDF y envíalo cuando estés listo.</p>
                                        )}
                                        <button onClick={sendToClient} disabled={busy === 'send'} data-testid="send-to-client-btn"
                                            className="w-full px-3 py-2.5 text-sm font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                            style={{ background: ACCENT }}>
                                            {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            {full.request.result_sent_at ? 'Reenviar al cliente' : 'Enviar dictamen al cliente'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
